import React, { useState } from 'react';
import travelieTheme from '../../../styles/travelieTheme';
import { useResponsive } from '../../../hooks/useResponsive';

// Phase 6: Migrated to TravelieTheme - June 16, 2026

const GroupForm = ({ onClose, onSubmit }) => {
  const { isMobile } = useResponsive();

  // Helper function for button styling
  const getButtonStyle = (type) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      add: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF', fontSize: travelieTheme.typography.fontSize.xs },
      remove: { ...baseStyle, background: travelieTheme.colors.status.error, color: '#FFFFFF', fontSize: travelieTheme.typography.fontSize.xs }
    };

    return styles[type] || styles.primary;
  };

  // Helper styles for form elements
  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: travelieTheme.spacing.xs
  };

  const labelStyle = {
    fontSize: travelieTheme.typography.fontSize.sm,
    fontWeight: travelieTheme.typography.fontWeight.medium,
    color: travelieTheme.colors.text.primary
  };

  const inputStyle = {
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };
  const [formData, setFormData] = useState({
    title: '',
    destination: '',
    start_date: '',
    end_date: '',
    component_type: 'cruise',
    max_participants: '',
    packages: [
      {
        id: 1,
        name: '',
        description: '',
        price: '',
        inclusions: []
      }
    ],
    payment_schedule: [
      {
        id: 1,
        name: 'Deposit',
        percentage: 30,
        due_date: ''
      }
    ],
    coordinator: {
      name: '',
      email: '',
      phone: ''
    },
    cancellation_policy: ''
  });

  const [currentInclusion, setCurrentInclusion] = useState('');

  const componentTypes = [
    { value: 'cruise', label: 'Cruise' },
    { value: 'tour', label: 'Tour/Activity' },
    { value: 'hotel', label: 'Hotel Package' },
    { value: 'flight', label: 'Flight Package' },
    { value: 'all-inclusive', label: 'All-Inclusive Resort' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.destination || !formData.start_date || !formData.end_date) {
      alert('Please fill in all required fields');
      return;
    }

    // Validate at least one package
    if (!formData.packages[0].name || !formData.packages[0].price) {
      alert('Please add at least one package with name and price');
      return;
    }

    onSubmit(formData);
  };

  const handlePackageChange = (index, field, value) => {
    const updatedPackages = [...formData.packages];
    updatedPackages[index][field] = value;
    setFormData({ ...formData, packages: updatedPackages });
  };

  const addPackage = () => {
    setFormData({
      ...formData,
      packages: [
        ...formData.packages,
        {
          id: formData.packages.length + 1,
          name: '',
          description: '',
          price: '',
          inclusions: []
        }
      ]
    });
  };

  const removePackage = (index) => {
    if (formData.packages.length > 1) {
      const updatedPackages = formData.packages.filter((_, i) => i !== index);
      setFormData({ ...formData, packages: updatedPackages });
    }
  };

  const addInclusion = (packageIndex) => {
    if (currentInclusion.trim()) {
      const updatedPackages = [...formData.packages];
      updatedPackages[packageIndex].inclusions.push(currentInclusion.trim());
      setFormData({ ...formData, packages: updatedPackages });
      setCurrentInclusion('');
    }
  };

  const removeInclusion = (packageIndex, inclusionIndex) => {
    const updatedPackages = [...formData.packages];
    updatedPackages[packageIndex].inclusions.splice(inclusionIndex, 1);
    setFormData({ ...formData, packages: updatedPackages });
  };

  const handlePaymentScheduleChange = (index, field, value) => {
    const updatedSchedule = [...formData.payment_schedule];
    updatedSchedule[index][field] = value;
    setFormData({ ...formData, payment_schedule: updatedSchedule });
  };

  const addPaymentSchedule = () => {
    setFormData({
      ...formData,
      payment_schedule: [
        ...formData.payment_schedule,
        {
          id: formData.payment_schedule.length + 1,
          name: '',
          percentage: 0,
          due_date: ''
        }
      ]
    });
  };

  const removePaymentSchedule = (index) => {
    if (formData.payment_schedule.length > 1) {
      const updatedSchedule = formData.payment_schedule.filter((_, i) => i !== index);
      setFormData({ ...formData, payment_schedule: updatedSchedule });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        padding: travelieTheme.spacing.lg,
        borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h2 style={{
          fontSize: travelieTheme.typography.fontSize.lg,
          fontWeight: travelieTheme.typography.fontWeight.semibold,
          color: travelieTheme.colors.text.primary,
          margin: 0
        }}>Create Group Booking</h2>
        <button 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: travelieTheme.colors.text.secondary,
            padding: 0,
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `color ${travelieTheme.transitions.fast}`
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.text.secondary}
        >×</button>
      </div>

      <form onSubmit={handleSubmit} style={{
        padding: travelieTheme.spacing.lg,
        maxHeight: 'calc(90vh - 140px)',
        overflowY: 'auto'
      }}>
        {/* Basic Information */}
        <div style={{ marginBottom: travelieTheme.spacing.xl }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.md,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.md
          }}>Basic Information</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: travelieTheme.spacing.md
          }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Group Title *</label>
              <input style={inputStyle}
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Caribbean Cruise Adventure 2025"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Destination *</label>
              <input style={inputStyle}
                type="text"
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                placeholder="e.g., Eastern Caribbean"
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Component Type *</label>
              <select style={inputStyle}
                value={formData.component_type}
                onChange={(e) => setFormData({ ...formData, component_type: e.target.value })}
                required
              >
                {componentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Max Participants</label>
              <input style={inputStyle}
                type="number"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                placeholder="Leave blank for unlimited"
                min="1"
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Start Date *</label>
              <input style={inputStyle}
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>End Date *</label>
              <input style={inputStyle}
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        {/* Packages */}
        <div style={{ marginBottom: travelieTheme.spacing.xl }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: travelieTheme.spacing.md
          }}>
            <h3 style={{
              fontSize: travelieTheme.typography.fontSize.md,
              fontWeight: travelieTheme.typography.fontWeight.semibold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>Packages</h3>
            <button 
              type="button" 
              onClick={addPackage}
              style={getButtonStyle('add')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              + Add Package
            </button>
          </div>
          
          {formData.packages.map((pkg, index) => (
            <div key={pkg.id} style={{
              padding: travelieTheme.spacing.md,
              background: '#F9FAFB',
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              borderRadius: travelieTheme.components.card.borderRadius,
              marginBottom: travelieTheme.spacing.md
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: travelieTheme.spacing.md
              }}>
                <h4 style={{
                  fontSize: travelieTheme.typography.fontSize.sm,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary,
                  margin: 0
                }}>Package {index + 1}</h4>
                {formData.packages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePackage(index)}
                    style={getButtonStyle('remove')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: travelieTheme.spacing.md
              }}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Package Name *</label>
                  <input style={inputStyle}
                    type="text"
                    value={pkg.name}
                    onChange={(e) => handlePackageChange(index, 'name', e.target.value)}
                    placeholder="e.g., Balcony Stateroom"
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Price per Person *</label>
                  <input style={inputStyle}
                    type="number"
                    value={pkg.price}
                    onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div style={{...formGroupStyle, gridColumn: isMobile ? "1" : "1 / -1"}}>
                  <label style={labelStyle}>Description</label>
                  <textarea style={{...inputStyle, minHeight: "80px", resize: "vertical"}}
                    value={pkg.description}
                    onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                    placeholder="Package details and features..."
                    rows="3"
                  />
                </div>

                <div style={{...formGroupStyle, gridColumn: isMobile ? "1" : "1 / -1"}}>
                  <label style={labelStyle}>Inclusions</label>
                  <div style={{
                    display: 'flex',
                    gap: travelieTheme.spacing.xs,
                    marginBottom: travelieTheme.spacing.sm
                  }}>
                    <input style={{...inputStyle, flex: 1}}
                      type="text"
                      value={currentInclusion}
                      onChange={(e) => setCurrentInclusion(e.target.value)}
                      placeholder="Add inclusion..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addInclusion(index);
                        }
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                      onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
                    />
                    <button
                      type="button"
                      onClick={() => addInclusion(index)}
                      style={getButtonStyle('add')}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Add
                    </button>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: travelieTheme.spacing.xs
                  }}>
                    {pkg.inclusions.map((inclusion, i) => (
                      <span key={i} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.xs,
                        padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                        background: '#DBEAFE',
                        color: '#1E40AF',
                        borderRadius: travelieTheme.components.card.borderRadius,
                        fontSize: travelieTheme.typography.fontSize.xs
                      }}>
                        {inclusion}
                        <button
                          type="button"
                          onClick={() => removeInclusion(index, i)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: '#1E40AF',
                            fontSize: '16px',
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Payment Schedule */}
        <div style={{ marginBottom: travelieTheme.spacing.xl }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: travelieTheme.spacing.md
          }}>
            <h3 style={{
              fontSize: travelieTheme.typography.fontSize.md,
              fontWeight: travelieTheme.typography.fontWeight.semibold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>Payment Schedule</h3>
            <button 
              type="button" 
              onClick={addPaymentSchedule}
              style={getButtonStyle('add')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              + Add Payment
            </button>
          </div>
          
          {formData.payment_schedule.map((payment, index) => (
            <div key={payment.id} style={{
              padding: travelieTheme.spacing.md,
              background: '#F9FAFB',
              border: `1px solid ${travelieTheme.colors.neutral.border}`,
              borderRadius: travelieTheme.components.card.borderRadius,
              marginBottom: travelieTheme.spacing.md
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: travelieTheme.spacing.md
              }}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Payment Name</label>
                  <input style={inputStyle}
                    type="text"
                    value={payment.name}
                    onChange={(e) => handlePaymentScheduleChange(index, 'name', e.target.value)}
                    placeholder="e.g., Deposit, Final Payment"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Percentage</label>
                  <input style={inputStyle}
                    type="number"
                    value={payment.percentage}
                    onChange={(e) => handlePaymentScheduleChange(index, 'percentage', e.target.value)}
                    placeholder="30"
                    min="0"
                    max="100"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Due Date</label>
                  <input style={inputStyle}
                    type="date"
                    value={payment.due_date}
                    onChange={(e) => handlePaymentScheduleChange(index, 'due_date', e.target.value)}
                  />
                </div>

              </div>
              {formData.payment_schedule.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePaymentSchedule(index)}
                  style={{...getButtonStyle('remove'), marginTop: travelieTheme.spacing.sm}}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Coordinator Information */}
        <div style={{ marginBottom: travelieTheme.spacing.xl }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.md,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.md
          }}>Group Coordinator</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: travelieTheme.spacing.md
          }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Coordinator Name</label>
              <input style={inputStyle}
                type="text"
                value={formData.coordinator.name}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, name: e.target.value }
                })}
                placeholder="Group leader name"
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Coordinator Email</label>
              <input style={inputStyle}
                type="email"
                value={formData.coordinator.email}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, email: e.target.value }
                })}
                placeholder="coordinator@email.com"
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Coordinator Phone</label>
              <input style={inputStyle}
                type="tel"
                value={formData.coordinator.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  coordinator: { ...formData.coordinator, phone: e.target.value }
                })}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Cancellation Policy */}
        <div style={{ marginBottom: travelieTheme.spacing.xl }}>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.md,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.md
          }}>Cancellation Policy</h3>
          <div style={{...formGroupStyle, gridColumn: isMobile ? "1" : "1 / -1"}}>
            <textarea style={{...inputStyle, minHeight: "80px", resize: "vertical"}}
              value={formData.cancellation_policy}
              onChange={(e) => setFormData({ ...formData, cancellation_policy: e.target.value })}
              placeholder="Enter cancellation policy details..."
              rows="5"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: travelieTheme.spacing.sm,
          paddingTop: travelieTheme.spacing.lg,
          borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
        }}>
          <button 
            type="button" 
            onClick={onClose}
            style={getButtonStyle('secondary')}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            Cancel
          </button>
          <button 
            type="submit"
            style={getButtonStyle('primary')}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            Create Group Booking
          </button>
        </div>
      </form>
    </div>
  );
};

export default GroupForm;
